import { MessageOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { Button, FloatButton, Popconfirm, Space, Table, Typography } from "antd";
import message from "../../message";
import KnowledgeChatModal from "./KnowledgeChatModal";
import KnowledgeModal from "./KnowledgeModal";

const { Paragraph } = Typography;

export default function KnowledgePage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8080";
  const token = useMemo(() => localStorage.getItem("token"), []);

  const loadList = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const resp = await fetch(`${baseUrl}/manager/knowledge/list`, {
        headers: token ? { token } : {},
      });
      const result = await resp.json();
      if (result?.code) {
        message.toast(result?.msg || "加载失败");
        return;
      }
      setList(result?.data?.list || []);
    } catch (error) {
      message.toast("加载失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setModalOpen(true);
  };

  const handleSave = async (values) => {
    if (saving) return;
    setSaving(true);
    try {
      const params = new URLSearchParams({
        title: values.title,
        content: values.content,
      });
      if (editing?.id) {
        params.set("id", editing.id);
      }
      const path = editing?.id ? "/manager/knowledge/update" : "/manager/knowledge/add";
      const resp = await fetch(
        editing?.id ? `${baseUrl}${path}?${params.toString()}` : `${baseUrl}${path}`,
        editing?.id
          ? {
              headers: token ? { token } : {},
            }
          : {
              method: "POST",
              headers: token ? { token } : {},
              body: params,
            }
      );
      const result = await resp.json();
      if (result?.code) {
        message.toast(result?.msg || "保存失败");
        return;
      }
      message.success("保存成功");
      setModalOpen(false);
      setEditing(null);
      await loadList();
    } catch (error) {
      message.toast("保存失败");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      const params = new URLSearchParams({ id: record.id });
      const resp = await fetch(`${baseUrl}/manager/knowledge/delete?${params.toString()}`, {
        headers: token ? { token } : {},
      });
      const result = await resp.json();
      if (result?.code) {
        message.toast(result?.msg || "删除失败");
        return;
      }
      message.success("删除成功");
      setList((prev) => prev.filter((item) => item.id !== record.id));
    } catch (error) {
      message.toast("删除失败");
    }
  };

  const handleAsk = async (question) => {
    const content = question.trim();
    if (!content || chatLoading) return;

    setChatLoading(true);
    setChatMessages((prev) => [...prev, { role: "user", content }]);
    try {
      const params = new URLSearchParams({ question: content });
      const resp = await fetch(`${baseUrl}/manager/knowledge/ask?${params.toString()}`, {
        headers: token ? { token } : {},
      });
      const result = await resp.json();
      if (result?.code) {
        message.toast(result?.msg || "发送失败");
        setChatMessages((prev) => prev.slice(0, -1));
        return;
      }
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: result?.data?.answer || "None",
        },
      ]);
    } catch (error) {
      message.toast("发送失败");
      setChatMessages((prev) => prev.slice(0, -1));
    } finally {
      setChatLoading(false);
    }
  };

  const columns = [
    {
      title: "序号",
      key: "index",
      width: 80,
      align: "center",
      render: (_value, _record, index) => index + 1,
    },
    {
      title: "标题",
      dataIndex: "title",
      key: "title",
      width: 240,
      align: "center",
      render: (value) => <span style={{ display: "block", fontWeight: 500, textAlign: "center" }}>{value || "-"}</span>,
    },
    {
      title: "内容",
      dataIndex: "content",
      key: "content",
      align: "center",
      render: (value) => (
        <Paragraph
          ellipsis={{ rows: 3, expandable: true, symbol: "展开" }}
          style={{ marginBottom: 0, whiteSpace: "pre-wrap", textAlign: "center" }}
        >
          {String(value || "-")}
        </Paragraph>
      ),
    },
    {
      title: "创建时间",
      dataIndex: "createTime",
      key: "createTime",
      width: 180,
      align: "center",
      render: (value) => value || "-",
    },
    {
      title: "操作",
      key: "action",
      width: 160,
      align: "center",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确认删除该知识？" onConfirm={() => handleDelete(record)}>
            <Button type="link" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Button type="primary" onClick={openAdd}>
          +新增知识
        </Button>
        <Button onClick={loadList} loading={loading}>
          刷新
        </Button>
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={list}
        loading={loading}
        bordered
        pagination={{ pageSize: 10 }}
        className="manager-knowledge-table"
      />
      <FloatButton
        type="primary"
        icon={<MessageOutlined />}
        tooltip="知识库对话"
        onClick={() => setChatOpen(true)}
      />
      <KnowledgeModal
        open={modalOpen}
        saving={saving}
        record={editing}
        onCancel={() => {
          if (saving) return;
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSave}
      />
      <KnowledgeChatModal
        open={chatOpen}
        loading={chatLoading}
        messages={chatMessages}
        onCancel={() => {
          if (chatLoading) return;
          setChatOpen(false);
        }}
        onSubmit={handleAsk}
        onClear={() => setChatMessages([])}
      />
    </div>
  );
}
